"use client";

import { useState, useEffect } from "react";
import { supabase } from "../../supabaseClient";
import { useAuth } from "../../context/AuthContext";
import {
  applyInstitutionFilter,
  ensureInstitutionOnPayload,
} from "../../lib/tenant";
import { autoVincularProfessorES } from "../../services/servicoProfessorDisciplina";
import { useNotificacao, ContainerNotificacoes } from "../common/AlertaNotificacao";
import { ValidadorRestrices } from "../../agendamento/ValidadorRestrices";
import "../../styles/HorarioEditor.css";
import "../../styles/HorarioEditor-novo-turma.css";

const DIAS_SEMANA = [
  { valor: "seg", nome: "Segunda" },
  { valor: "ter", nome: "Terça" },
  { valor: "qua", nome: "Quarta" },
  { valor: "qui", nome: "Quinta" },
  { valor: "sex", nome: "Sexta" },
];

const TEMPOS_MANHA = [
  { inicio: "07:30", fim: "08:15", label: "1º Tempo" },
  { inicio: "08:15", fim: "09:00", label: "2º Tempo" },
  { inicio: "09:05", fim: "09:50", label: "3º Tempo" },
  { inicio: "09:50", fim: "10:35", label: "4º Tempo" },
  { inicio: "10:50", fim: "11:35", label: "5º Tempo" },
  { inicio: "11:35", fim: "12:20", label: "6º Tempo" },
];

const TEMPOS_TARDE = [
  { inicio: "13:00", fim: "13:45", label: "1º Tempo" },
  { inicio: "13:45", fim: "14:30", label: "2º Tempo" },
  { inicio: "14:35", fim: "15:20", label: "3º Tempo" },
  { inicio: "15:20", fim: "16:05", label: "4º Tempo" },
  { inicio: "16:10", fim: "16:55", label: "5º Tempo" },
  { inicio: "16:55", fim: "17:40", label: "6º Tempo" },
];

const TEMPOS_NOITE = [
  { inicio: "18:00", fim: "18:45", label: "1º Tempo" },
  { inicio: "18:45", fim: "19:30", label: "2º Tempo" },
  { inicio: "19:35", fim: "20:20", label: "3º Tempo" },
  { inicio: "20:20", fim: "21:05", label: "4º Tempo" },
  { inicio: "21:10", fim: "21:55", label: "5º Tempo" },
  { inicio: "21:55", fim: "22:40", label: "6º Tempo" },
];

export default function HorarioEditor({ turmaId, onClose, onSave }) {
  const { isAdmin, instituicaoId } = useAuth();
  const notificacao = useNotificacao();
  const [horarios, setHorarios] = useState({});
  const [disciplinas, setDisciplinas] = useState([]);
  const [disciplinasTurma, setDisciplinasTurma] = useState([]);
  const [professorDisciplina, setProfessorDisciplina] = useState({});
  const [turmas, setTurmas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [atualizandoProfessores, setAtualizandoProfessores] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [turma, setTurma] = useState(null);
  const [conflitos, setConflitos] = useState([]);
  const [needsSave, setNeedsSave] = useState(false);
  const [selectedTurmaId, setSelectedTurmaId] = useState(turmaId);
  const [turnoSelecionado, setTurnoSelecionado] = useState("manha");
  const [disciplinaProfessorTurma, setDisciplinaProfessorTurma] = useState({});
  const [error, setError] = useState(null);
  const [horariosOriginais, setHorariosOriginais] = useState({}); // Para rastrear horários originais
  const [showNovasTurmas, setShowNovasTurmas] = useState(false);
  const [novasTurmasSelecionadas, setNovasTurmasSelecionadas] = useState([]);

  useEffect(() => {
    setSelectedTurmaId(turmaId);
  }, [turmaId]);

  const getTemposPorTurno = (turno) => {
    switch (turno) {
      case "manha":
        return TEMPOS_MANHA;
      case "tarde":
        return TEMPOS_TARDE;
      case "noite":
        return TEMPOS_NOITE;
      default:
        return TEMPOS_MANHA;
    }
  };

  const normalizarHora = (hora) => {
    if (!hora) return hora;
    if (typeof hora !== "string") return hora;
    const partes = hora.split(":");
    if (partes.length < 2) return hora;
    return `${partes[0].padStart(2, "0")}:${partes[1].padStart(2, "0")}`;
  };

  const TEMPOS = getTemposPorTurno(turnoSelecionado);

  const carregarDadosTurma = async (turmaIdParam, discDataParam = null) => {
    const discData = discDataParam || disciplinas;

    const { data: turmaData } = await supabase
      .from("turmas")
      .select("*, sala:sala_id(id, nome), curso:curso_id(id, nome)")
      .eq("id", turmaIdParam)
      .single();

    setTurma(turmaData);

    if (turmaData?.turno) {
      const turnoMap = { manhã: "manha", tarde: "tarde", noite: "noite" };
      setTurnoSelecionado(turnoMap[turmaData.turno] || turmaData.turno);
    }

    // Buscar disciplinas vinculadas à turma COM tempos_semanais
    const { data: tdData } = await supabase
      .from("turma_disciplina")
      .select("disciplina_id, tempos_semanais")
      .eq("turma_id", turmaIdParam);

    const discIdsTurma = (tdData || []).map((t) => t.disciplina_id);
    const discsTurma = (discData || [])
      .filter((d) => discIdsTurma.includes(d.id))
      .map((d) => {
        // Adicionar tempos_semanais ao objeto disciplina
        const temposData = tdData.find(td => td.disciplina_id === d.id);
        return {
          ...d,
          tempos_semanais: temposData?.tempos_semanais || d.carga_horaria_semanal || 2
        };
      });
    setDisciplinasTurma(discsTurma.length > 0 ? discsTurma : discData || []);

    const { data: horariosData } = await supabase
      .from("horarios")
      .select(
        `
        *,
        disciplina:disciplina_id(id, nome, codigo, tipo, importancia, carga_horaria_semanal),
        professor:professor_id(id, nome),
        turma:turma_id(id, nome, sala:sala_id(id, nome))
      `,
      )
      .eq("turma_id", turmaIdParam);

    const discProfMap = {};
    (horariosData || []).forEach((h) => {
      if (h.disciplina_id && h.professor_id && !discProfMap[h.disciplina_id]) {
        discProfMap[h.disciplina_id] = h.professor_id;
      }
    });
    setDisciplinaProfessorTurma(discProfMap);

    // Organizar horários por dia/hora para exibir na grade
    const horariosOrganizados = {};
    (horariosData || []).forEach((h) => {
      const horaInicio = normalizarHora(h.hora_inicio);
      const horaFim = normalizarHora(h.hora_fim);
      const chave = `${h.dia_semana}-${horaInicio}`;
      horariosOrganizados[chave] = {
        ...h,
        hora_inicio: horaInicio,
        hora_fim: horaFim,
        disciplina: h.disciplina,
        professor: h.professor,
        turma: h.turma,
      };
    });
    setHorarios(horariosOrganizados);
    setHorariosOriginais(JSON.parse(JSON.stringify(horariosOrganizados))); // Salvar cópia profunda dos originais
    setNeedsSave(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!turmaId) {
          setError("Turma inválida. Selecione uma turma para editar.");
          setLoading(false);
          return;
        }

        // Buscar todas as disciplinas
        let discQuery = supabase.from("disciplinas").select("*").order("nome");
        discQuery = applyInstitutionFilter(discQuery, isAdmin, instituicaoId);
        const { data: discData } = await discQuery;
        setDisciplinas(discData || []);

        // Buscar professor_disciplina
        let pdQuery = supabase
          .from("professor_disciplina")
          .select("*, professor:professor_id(id, nome)");
        pdQuery = applyInstitutionFilter(pdQuery, isAdmin, instituicaoId);
        const { data: pdData } = await pdQuery;

        const pdMap = {};
        (pdData || []).forEach((pd) => {
          if (!pdMap[pd.disciplina_id]) pdMap[pd.disciplina_id] = [];
          if (pd.professor) pdMap[pd.disciplina_id].push(pd.professor);
        });
        setProfessorDisciplina(pdMap);

        // Buscar turmas
        let turmasQuery = supabase
          .from("turmas")
          .select("*, sala:sala_id(id, nome), curso:curso_id(id, nome)")
          .order("nome");
        turmasQuery = applyInstitutionFilter(
          turmasQuery,
          isAdmin,
          instituicaoId,
        );
        const { data: turmasData } = await turmasQuery;
        setTurmas(turmasData || []);

        // Buscar turma atual
        if (selectedTurmaId) {
          await carregarDadosTurma(selectedTurmaId, discData);
        }
      } catch (err) {
        console.error("Erro ao carregar dados:", err.message);
        setError("Erro ao carregar dados: " + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turmaId, isAdmin, instituicaoId, selectedTurmaId]);

  const handleTurmaChange = async (newTurmaId) => {
    setSelectedTurmaId(newTurmaId);
    setHorarios({});
    setHorariosOriginais({}); // Resetar originais também
    setDisciplinaProfessorTurma({});

    if (newTurmaId) {
      await carregarDadosTurma(newTurmaId);
      // Auto-atualizar professores após carregar dados
      await atualizarProfessoresVinculados(newTurmaId);
    } else {
      setTurma(null);
      setDisciplinasTurma([]);
    }
  };

  async function atualizarProfessoresVinculados(turmaIdParam) {
    try {
      setAtualizandoProfessores(true);
      const resultado = await autoVincularProfessorES(
        turmaIdParam,
        isAdmin,
        instituicaoId,
      );

      if (resultado.sucesso && resultado.atualizado > 0) {
        const msg = `✓ ${resultado.mensagem}`;
        notificacao.sucesso(msg);
        setConflitos((prev) => [
          {
            tipo: "sucesso",
            mensagem: msg,
          },
          ...prev,
        ]);

        // Recarregar dados após atualização
        await new Promise((resolve) => setTimeout(resolve, 500));
        await carregarDadosTurma(turmaIdParam);
      } else {
        notificacao.aviso("Professores já estão vinculados - nenhuma atualização necessária");
      }
    } catch (err) {
      console.error("Erro ao atualizar professores:", err);
      notificacao.erro(`Erro ao atualizar professores: ${err.message}`);
    } finally {
      setAtualizandoProfessores(false);
    }
  }

  const adicionarNovasTurmas = async () => {
    if (novasTurmasSelecionadas.length === 0) {
      notificacao.aviso("Selecione pelo menos uma turma para adicionar ao editor");
      return;
    }

    try {
      setSaving(true);
      
      for (const turmaIdNova of novasTurmasSelecionadas) {
        await carregarDadosTurma(turmaIdNova);
        notificacao.sucesso(`Turma adicionada para edição`);
      }

      setShowNovasTurmas(false);
      setNovasTurmasSelecionadas([]);
      setSelectedTurmaId(novasTurmasSelecionadas[0]);
      notificacao.sucesso("Novas turmas adicionadas com sucesso!");
    } catch (err) {
      console.error("Erro ao adicionar novas turmas:", err);
      notificacao.erro(`Erro ao adicionar turmas: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDragStart = (e, item, origem) => {
    setDraggedItem({ ...item, origem });
    e.dataTransfer.effectAllowed = "move";
    e.target.classList.add("dragging");
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add("drag-over");
  };

  const handleDragLeave = (e) => {
    e.currentTarget.classList.remove("drag-over");
  };

  const handleDrop = async (e, dia, tempo) => {
    e.preventDefault();
    e.currentTarget.classList.remove("drag-over");

    if (!draggedItem) return;

    const chaveDestino = `${dia}-${tempo.inicio}`;
    const horarioExistente = horarios[chaveDestino];

    if (draggedItem.origem === "paleta") {
      let professorAuto = null;
      const profJaAtribuido = disciplinaProfessorTurma[draggedItem.id];

      if (profJaAtribuido) {
        // Usar o mesmo professor já atribuído
        const profs = professorDisciplina[draggedItem.id] || [];
        professorAuto = profs.find((p) => p.id === profJaAtribuido) || null;
      } else {
        // Atribuir primeiro professor vinculado
        const professoresVinculados = professorDisciplina[draggedItem.id] || [];
        professorAuto =
          professoresVinculados.length > 0 ? professoresVinculados[0] : null;

        // Guardar qual professor foi atribuído
        if (professorAuto) {
          setDisciplinaProfessorTurma((prev) => ({
            ...prev,
            [draggedItem.id]: professorAuto.id,
          }));
        }
      }

      const novoHorario = {
        ...(horarioExistente?.id ? { id: horarioExistente.id } : {}),
        turma_id: selectedTurmaId,
        disciplina_id: draggedItem.id,
        professor_id: professorAuto?.id || null,
        dia_semana: dia,
        hora_inicio: tempo.inicio,
        hora_fim: tempo.fim,
        disciplina: draggedItem,
        professor: professorAuto,
        turma: turma,
      };

      setHorarios((prev) => ({
        ...prev,
        [chaveDestino]: novoHorario,
      }));
    } else if (draggedItem.origem === "celula") {
      const chaveOrigem = `${draggedItem.dia_semana}-${draggedItem.hora_inicio}`;

      const horarioOrigem = { ...horarios[chaveOrigem] };
      const horarioDestino = horarios[chaveDestino]
        ? { ...horarios[chaveDestino] }
        : null;

      horarioOrigem.dia_semana = dia;
      horarioOrigem.hora_inicio = tempo.inicio;
      horarioOrigem.hora_fim = tempo.fim;

      if (horarioDestino) {
        horarioDestino.dia_semana = draggedItem.dia_semana;
        horarioDestino.hora_inicio = draggedItem.hora_inicio;
        horarioDestino.hora_fim = draggedItem.hora_fim;
      }

      setHorarios((prev) => {
        const novo = { ...prev };
        novo[chaveDestino] = horarioOrigem;
        if (horarioDestino) {
          novo[chaveOrigem] = horarioDestino;
        } else {
          delete novo[chaveOrigem];
        }
        return novo;
      });
    }

    setDraggedItem(null);
    setNeedsSave(true);
  };

  const handleDragEnd = (e) => {
    e.target.classList.remove("dragging");
    setDraggedItem(null);
    document.querySelectorAll(".drag-over").forEach((el) => {
      el.classList.remove("drag-over");
    });
  };

  const removerHorario = (dia, hora) => {
    const chave = `${dia}-${hora}`;
    setHorarios((prev) => {
      const novo = { ...prev };
      delete novo[chave];
      return novo;
    });
    setNeedsSave(true);
  };

  const gerarHorariosAutomatico = async () => {
    if (!selectedTurmaId || disciplinasTurma.length === 0) {
      notificacao.aviso("Selecione uma turma com disciplinas para gerar horários");
      return;
    }

    try {
      // Calcular quantos tempos são necessários usando tempos_semanais (não carga_horaria_semanal)
      const totalTemposNecessarios = disciplinasTurma.reduce(
        (acc, d) => acc + (d.tempos_semanais || d.carga_horaria_semanal || 2),
        0
      );

      const totalTemposDisponiveis = DIAS_SEMANA.length * TEMPOS.length; // 5 dias x 6 tempos = 30

      if (totalTemposNecessarios > totalTemposDisponiveis) {
        notificacao.erro(
          `Não há tempo suficiente. Disciplinas precisam de ${totalTemposNecessarios} tempos, mas estão disponíveis apenas ${totalTemposDisponiveis} (5 dias x ${TEMPOS.length} períodos). Reduza os tempos das disciplinas.`,
          { autoClose: false, duracao: 10000 }
        );
        return;
      }

      // Embaralhar disciplinas para distribuição aleatória
      const discOrdenadas = [...disciplinasTurma].sort(() => Math.random() - 0.5);
      
      // Criar lista de slots disponíveis (dia, indice do tempo)
      const slots = [];
      DIAS_SEMANA.forEach((dia) => {
        TEMPOS.forEach((tempo, idx) => {
          slots.push({ dia: dia.valor, tempoIdx: idx, tempo });
        });
      });

      // Embaralhar slots também
      const slotsEmbaralrados = [...slots].sort(() => Math.random() - 0.5);
      const slotsUsados = new Set();

      // Distribui horários respeitando tempos_semanais
      const novosHorarios = {};
      const disciplinasComTemposAlocados = {}; // Rastrear quantos tempos foram alocados
      
      for (const disc of discOrdenadas) {
        const temposNecessarios = disc.tempos_semanais || disc.carga_horaria_semanal || 2;
        disciplinasComTemposAlocados[disc.id] = 0;
        let temposAlocados = 0;

        // Tentar alocar tempos para esta disciplina
        for (let i = 0; i < slotsEmbaralrados.length && temposAlocados < temposNecessarios; i++) {
          const slot = slotsEmbaralrados[i];
          const chave = `${slot.dia}-${slot.tempo.inicio}`;

          // Verificar se slot já foi usado
          if (!slotsUsados.has(chave) && !novosHorarios[chave] && !horarios[chave]) {
            const professorId = encontrarProfessorPadrao(disc.id);
            
            if (!professorId) {
              // Tentar encontrar qualquer professor vinculado
              const profsDisponiveis = professorDisciplina[disc.id] || [];
              if (profsDisponiveis.length === 0) {
                continue; // Sem professor, pular este slot
              }
            }

            novosHorarios[chave] = {
              turma_id: selectedTurmaId,
              disciplina_id: disc.id,
              professor_id: professorId || (professorDisciplina[disc.id]?.[0]?.id),
              dia_semana: slot.dia,
              hora_inicio: slot.tempo.inicio,
              hora_fim: slot.tempo.fim,
              disciplina: disc,
              professor: professorDisciplina[disc.id]?.find(p => p.id === (professorId || professorDisciplina[disc.id]?.[0]?.id)),
              turma: turma,
            };
            slotsUsados.add(chave);
            temposAlocados++;
            disciplinasComTemposAlocados[disc.id] = temposAlocados;
          }
        }

        if (temposAlocados < temposNecessarios) {
          notificacao.aviso(
            `Nem todos os tempos da disciplina "${disc.nome}" foram alocados (${temposAlocados} de ${temposNecessarios})`
          );
        }
      }

      // PREENCHIMENTO DE LACUNAS: Se houver slots vazios, preencher com disciplinas que têm menos tempos
      const slotsVazios = [];
      slotsEmbaralrados.forEach((slot) => {
        const chave = `${slot.dia}-${slot.tempo.inicio}`;
        if (!slotsUsados.has(chave) && !horarios[chave]) {
          slotsVazios.push(slot);
        }
      });

      if (slotsVazios.length > 0) {
        // Encontrar disciplinas que ainda têm tempos não alocados
        const discComTemposRestantes = discOrdenadas
          .filter(d => {
            const temposNecessarios = d.tempos_semanais || d.carga_horaria_semanal || 2;
            return disciplinasComTemposAlocados[d.id] < temposNecessarios;
          })
          .sort((a, b) => {
            // Ordenar por menor diferença (menor tempos restantes primeiro)
            const temposRestA = (a.tempos_semanais || a.carga_horaria_semanal || 2) - (disciplinasComTemposAlocados[a.id] || 0);
            const temposRestB = (b.tempos_semanais || b.carga_horaria_semanal || 2) - (disciplinasComTemposAlocados[b.id] || 0);
            return temposRestA - temposRestB;
          });

        // Preencher lacunas com essas disciplinas
        for (let i = 0; i < slotsVazios.length && discComTemposRestantes.length > 0; i++) {
          const slot = slotsVazios[i];
          const chave = `${slot.dia}-${slot.tempo.inicio}`;
          
          // Procurar disciplina que ainda precisa de tempos
          let discParaPreencher = null;
          for (const disc of discComTemposRestantes) {
            const temposNecessarios = disc.tempos_semanais || disc.carga_horaria_semanal || 2;
            if (disciplinasComTemposAlocados[disc.id] < temposNecessarios) {
              discParaPreencher = disc;
              break;
            }
          }

          if (discParaPreencher) {
            const professorId = encontrarProfessorPadrao(discParaPreencher.id);
            const profsDisponiveis = professorDisciplina[discParaPreencher.id] || [];
            
            if (professorId || profsDisponiveis.length > 0) {
              novosHorarios[chave] = {
                turma_id: selectedTurmaId,
                disciplina_id: discParaPreencher.id,
                professor_id: professorId || profsDisponiveis[0]?.id,
                dia_semana: slot.dia,
                hora_inicio: slot.tempo.inicio,
                hora_fim: slot.tempo.fim,
                disciplina: discParaPreencher,
                professor: profsDisponiveis.find(p => p.id === (professorId || profsDisponiveis[0]?.id)),
                turma: turma,
              };
              slotsUsados.add(chave);
              disciplinasComTemposAlocados[discParaPreencher.id]++;
            }
          }
        }
      }

      // Atualizar horários
      setHorarios((prev) => ({
        ...prev,
        ...novosHorarios,
      }));

      setNeedsSave(true);
      const lacunasPreenchidas = Object.keys(novosHorarios).length - (totalTemposNecessarios);
      notificacao.sucesso(`✓ Horários gerados! ${Object.keys(novosHorarios).length} períodos preenchidos${lacunasPreenchidas > 0 ? ` (${lacunasPreenchidas} lacunas preenchidas)` : ''}.`);
    } catch (err) {
      console.error("Erro ao gerar horários:", err);
      notificacao.erro(`Erro ao gerar horários: ${err.message}`);
    }
  };

  const encontrarProfessorPadrao = (disciplinaId) => {
    if (!disciplinaId) return null;
    if (disciplinaProfessorTurma[disciplinaId])
      return disciplinaProfessorTurma[disciplinaId];
    const profs = professorDisciplina[disciplinaId] || [];
    return profs.length > 0 ? profs[0].id : null;
  };

  const salvarHorarios = async () => {
    setSaving(true);
    setConflitos([]);

    try {
      console.debug("salvarHorarios horarios atuais:", horarios);
      console.debug("salvarHorarios horarios originais:", horariosOriginais);

      // Verificar se há horários duplicados ou sobrepostos (mesmo dia e horário)
      const horariosList = Object.values(horarios).filter(
        (h) => h.disciplina_id,
      );

      // Função para verificar se dois intervalos se sobrepõem
      const intervalosSeSobrepoem = (h1, h2) => {
        const inicio1 = new Date(`2000-01-01T${h1.hora_inicio}`);
        const fim1 = new Date(`2000-01-01T${h1.hora_fim}`);
        const inicio2 = new Date(`2000-01-01T${h2.hora_inicio}`);
        const fim2 = new Date(`2000-01-01T${h2.hora_fim}`);

        return inicio1 < fim2 && inicio2 < fim1;
      };

      // 1️⃣ Verificar sobreposições DENTRO DA TURMA para cada dia da semana
      const diasSemana = ['seg', 'ter', 'qua', 'qui', 'sex'];
      const conflitosSobreposicao = [];

      diasSemana.forEach(dia => {
        const horariosDia = horariosList.filter(h => h.dia_semana === dia);
        for (let i = 0; i < horariosDia.length; i++) {
          for (let j = i + 1; j < horariosDia.length; j++) {
            if (intervalosSeSobrepoem(horariosDia[i], horariosDia[j])) {
              conflitosSobreposicao.push({
                dia,
                horario1: `${horariosDia[i].hora_inicio}-${horariosDia[i].hora_fim}`,
                horario2: `${horariosDia[j].hora_inicio}-${horariosDia[j].hora_fim}`,
                disciplina1: horariosDia[i].disciplina?.nome || 'Sem nome',
                disciplina2: horariosDia[j].disciplina?.nome || 'Sem nome'
              });
            }
          }
        }
      });

      if (conflitosSobreposicao.length > 0) {
        const conflitosMsg = conflitosSobreposicao
          .map((c) => `${c.dia}: ${c.disciplina1} (${c.horario1}) conflita com ${c.disciplina2} (${c.horario2})`)
          .join("; ");
        const mensagemErro = `Erro ao salvar horário: Conflito de horários na turma. A disciplina "${conflitosSobreposicao[0].disciplina1}" na turma ${turma?.nome || 'desconhecida'} se sobrepõe com "${conflitosSobreposicao[0].disciplina2}". Detalhe: ${conflitosMsg}. Reorganize os horários e tente novamente.`;
        notificacao.erro(mensagemErro, { autoClose: false, duracao: 10000 });
        throw new Error(mensagemErro);
      }

      // 2️⃣ Verificar horários que começam exatamente no mesmo horário (validação adicional)
      const horariosAgrupados = horariosList.reduce((acc, h) => {
        const chave = `${h.dia_semana}-${h.hora_inicio}`;
        if (!acc[chave]) acc[chave] = [];
        acc[chave].push(h);
        return acc;
      }, {});

      const duplicados = Object.values(horariosAgrupados).filter(
        (grupo) => grupo.length > 1,
      );
      if (duplicados.length > 0) {
        const conflitosMsg = duplicados
          .map((grupo) => {
            const primeiro = grupo[0];
            return `${primeiro.dia_semana} às ${primeiro.hora_inicio} (${grupo.length} disciplinas no mesmo horário)`;
          })
          .join("; ");
        const mensagemErro = `Erro ao salvar horário: Horários duplicados encontrados. Na turma ${turma?.nome || 'desconhecida'} há múltiplas disciplinas agendadas no mesmo horário: ${conflitosMsg}. Reorganize os horários e tente novamente.`;
        notificacao.erro(mensagemErro, { autoClose: false, duracao: 10000 });
        throw new Error(mensagemErro);
      }

      // 3️⃣ Verificar conflitos de professor com banco de dados (professor em dois lugares ao mesmo tempo)
      const conflitosProf = [];
      for (const horario of horariosList) {
        if (!horario.professor_id) continue;
        
        // Buscar outros horários do mesmo professor no mesmo dia/hora no banco
        const { data: outrosHorarios, error: errBusca } = await supabase
          .from("horarios")
          .select("id, turma_id, disciplina_id, dia_semana, hora_inicio, hora_fim, turma:turma_id(nome)")
          .eq("professor_id", horario.professor_id)
          .eq("dia_semana", horario.dia_semana)
          .neq("turma_id", selectedTurmaId);
        
        if (errBusca) continue;
        
        if (outrosHorarios) {
          for (const outro of outrosHorarios) {
            if (intervalosSeSobrepoem(horario, outro)) {
              conflitosProf.push({
                professor: horario.professor?.nome || "Desconhecido",
                dia: horario.dia_semana,
                disciplina1: horario.disciplina?.nome || "Sem nome",
                turma1: turma?.nome || "Desconhecida",
                horario1: `${horario.hora_inicio}-${horario.hora_fim}`,
                disciplina2: outro.disciplina_id,
                turma2: outro.turma?.nome || "Desconhecida",
                horario2: `${outro.hora_inicio}-${outro.hora_fim}`
              });
            }
          }
        }
      }

      if (conflitosProf.length > 0) {
        const conflitosMsg = conflitosProf
          .map((c) => `${c.professor} no ${c.dia} (${c.disciplina1} em ${c.turma1} ${c.horario1} conflita com ${c.turma2} ${c.horario2})`)
          .join("; ");
        const mensagemErro = `Erro ao salvar horário: Conflito de professor. ${conflitosProf[0].professor} não pode estar em duas turmas ao mesmo tempo. ${conflitosMsg}. Altere os horários e tente novamente.`;
        notificacao.erro(mensagemErro, { autoClose: false, duracao: 10000 });
        throw new Error(mensagemErro);
      }

      // 4️⃣ Verificar conflitos de sala
      const conflitosAula = [];
      for (const horario of horariosList) {
        if (!horario.turma?.sala?.id) continue;
        
        // Buscar outros horários na mesma sala no mesmo dia/hora
        const { data: outrosHorarios, error: errBusca } = await supabase
          .from("horarios")
          .select("id, turma_id, disciplina_id, dia_semana, hora_inicio, hora_fim, turma:turma_id(nome)")
          .eq("sala_id", horario.turma.sala.id)
          .eq("dia_semana", horario.dia_semana)
          .neq("turma_id", selectedTurmaId);
        
        if (errBusca) continue;
        
        if (outrosHorarios) {
          for (const outro of outrosHorarios) {
            if (intervalosSeSobrepoem(horario, outro)) {
              conflitosAula.push({
                sala: horario.turma.sala.nome,
                dia: horario.dia_semana,
                disciplina1: horario.disciplina?.nome || "Sem nome",
                turma1: turma?.nome || "Desconhecida",
                horario1: `${horario.hora_inicio}-${horario.hora_fim}`,
                disciplina2: outro.disciplina_id,
                turma2: outro.turma?.nome || "Desconhecida",
                horario2: `${outro.hora_inicio}-${outro.hora_fim}`
              });
            }
          }
        }
      }

      if (conflitosAula.length > 0) {
        const conflitosMsg = conflitosAula
          .map((c) => `Sala ${c.sala} no ${c.dia} (${c.disciplina1} ${c.horario1} conflita com ${c.turma2} ${c.horario2})`)
          .join("; ");
        const mensagemErro = `Erro ao salvar horário: Conflito de sala. ${conflitosAula[0].sala} não pode ser usada em dois horários ao mesmo tempo. ${conflitosMsg}. Altere os horários e tente novamente.`;
        notificacao.erro(mensagemErro, { autoClose: false, duracao: 10000 });
        throw new Error(mensagemErro);
      }

      // Preparar horários para salvar (novos + existentes em um único upsert)
      const horariosAtuais = Object.values(horarios).filter(
        (h) => h.disciplina_id,
      );
      const chavesAtuais = new Set(Object.keys(horarios));

      const horariosParaSalvar = [];
      const horariosParaDeletar = [];
      const horariosSemProfessor = [];
      const agora = new Date().toISOString();

      horariosAtuais.forEach((horarioAtual) => {
        const professorId =
          horarioAtual.professor_id ||
          encontrarProfessorPadrao(horarioAtual.disciplina_id);
        if (!professorId) {
          horariosSemProfessor.push(horarioAtual);
          return;
        }

        // Construir payload preservando o ID se existir
        const payload = {
          turma_id: selectedTurmaId,
          disciplina_id: horarioAtual.disciplina_id,
          professor_id: professorId,
          dia_semana: horarioAtual.dia_semana,
          hora_inicio: normalizarHora(String(horarioAtual.hora_inicio)),
          hora_fim: normalizarHora(String(horarioAtual.hora_fim)),
          sala_id: turma?.sala?.id || null,
          updated_at: agora,
          created_at: horarioAtual.created_at || agora, // Sempre incluir created_at
        };

        // Adicionar ID apenas se o horário já existe (para updates)
        // NÃO adicionar ID se for novo (deixar banco gerar)
        if (horarioAtual.id) {
          payload.id = horarioAtual.id;
        }

        horariosParaSalvar.push(
          ensureInstitutionOnPayload(payload, isAdmin, instituicaoId),
        );
      });

      if (horariosSemProfessor.length > 0) {
        const disciplinasSemProf = horariosSemProfessor
          .map((h) => h.disciplina?.nome || "Desconhecida")
          .join(", ");
        const mensagemErro = `Erro ao salvar horário: Não foi possível atribuir professor para as disciplinas: ${disciplinasSemProf}. Verifique os vínculos de professor-disciplina na turma ${turma?.nome || 'desconhecida'} e tente novamente.`;
        notificacao.erro(mensagemErro, { autoClose: false, duracao: 10000 });
        throw new Error(mensagemErro);
      }

      // Identificar horários a deletar (estavam nos originais mas não estão agora)
      Object.keys(horariosOriginais).forEach((chave) => {
        if (!chavesAtuais.has(chave)) {
          const horarioOriginal = horariosOriginais[chave];
          if (horarioOriginal?.id) {
            horariosParaDeletar.push(horarioOriginal.id);
          }
        }
      });

      // Separar horários novos (sem ID) de horários existentes (com ID)
      const horariosNovos = horariosParaSalvar.filter(h => !h.id);
      const horariosExistentes = horariosParaSalvar.filter(h => h.id);

      console.debug("Operações identificadas:", {
        novos: horariosNovos.length,
        existentes: horariosExistentes.length,
        deletar: horariosParaDeletar.length,
      });

      // Executar remoção de horários excluídos
      if (horariosParaDeletar.length > 0) {
        const { error: deleteError } = await supabase
          .from("horarios")
          .delete()
          .in("id", horariosParaDeletar);

        if (deleteError) {
          console.error("Erro ao deletar horários:", deleteError);
          throw deleteError;
        }
        console.log("Horários deletados:", horariosParaDeletar);
      }

      // OPERAÇÃO 1: Inserir horários novos (sem ID)
      if (horariosNovos.length > 0) {
        console.log("Inserindo horários novos:", horariosNovos.length);
        const { data: insertData, error: insertError } = await supabase
          .from("horarios")
          .insert(horariosNovos)
          .select();

        if (insertError) {
          console.error("Erro ao inserir horários novos:", insertError);
          console.error("Detalhes do erro:", {
            message: insertError.message,
            details: insertError.details,
            hint: insertError.hint,
            code: insertError.code
          });
          throw new Error(`Erro ao inserir horários novos: ${insertError.message}`);
        }
        console.log("Horários novos inseridos com sucesso:", insertData?.length || 0);
      }

      // OPERAÇÃO 2: Atualizar horários existentes (com ID)
      if (horariosExistentes.length > 0) {
        console.log("Atualizando horários existentes:", horariosExistentes.length);
        const { data: updateData, error: updateError } = await supabase
          .from("horarios")
          .upsert(horariosExistentes, { onConflict: "id" })
          .select();

        if (updateError) {
          console.error("Erro ao atualizar horários:", updateError);
          console.error("Detalhes do erro:", {
            message: updateError.message,
            details: updateError.details,
            hint: updateError.hint,
            code: updateError.code
          });
          throw new Error(`Erro ao atualizar horários: ${updateError.message}`);
        }
        console.log("Horários existentes atualizados com sucesso:", updateData?.length || 0);
      }

      // trigger parent update before alert so list refreshes immediately
      if (onSave) onSave();
      setNeedsSave(false);

      // Atualizar horários originais com o estado atual
      setHorariosOriginais(JSON.parse(JSON.stringify(horarios)));

      notificacao.sucesso("✓ Horários salvos com sucesso!");
      alert("Horários salvos com sucesso!");

      // also refresh the local state so editor shows persisted data
      if (selectedTurmaId) {
        await carregarDadosTurma(selectedTurmaId);
      }
    } catch (err) {
      console.error("Erro ao salvar:", err.message);

      let mensagemUsuario = "";
      
      // Erro específico de ID nulo
      if (err.message.includes('null value in column "id"')) {
        mensagemUsuario = "Erro ao salvar: Problema com geração de ID. Tente salvar novamente. Se o erro persistir, recarregue a página.";
        setConflitos([{
          tipo: "erro",
          mensagem: mensagemUsuario
        }]);
      }
      // Verificar se é erro de restrição de sobreposição
      else if (err.message.includes('conflicting key value violates exclusion constraint')) {
        if (err.message.includes('no_overlap_turma') || err.message.includes('exclude_turma_horario')) {
          mensagemUsuario = "Horários sobrepostos: A turma não pode ter duas aulas no mesmo horário. Verifique o agendamento.";
          setConflitos([{
            tipo: "erro",
            mensagem: mensagemUsuario
          }]);
        } else if (err.message.includes('exclude_professor_horario')) {
          mensagemUsuario = "Professor duplo: Um professor não pode estar em duas aulas ao mesmo tempo. Verifique a alocação.";
          setConflitos([{
            tipo: "erro",
            mensagem: mensagemUsuario
          }]);
        } else if (err.message.includes('exclude_sala_horario')) {
          mensagemUsuario = "Sala duplicada: Uma sala não pode ter duas aulas no mesmo horário. Verifique a ocupação.";
          setConflitos([{
            tipo: "erro",
            mensagem: mensagemUsuario
          }]);
        } else {
          mensagemUsuario = "Erro de restrição de horário. Verifique se não há conflitos de agendamento.";
          setConflitos([{
            tipo: "erro",
            mensagem: mensagemUsuario
          }]);
        }
      } else if (err.message.includes('null value in column "created_at"')) {
        mensagemUsuario = "Erro ao salvar: Data de criação inválida. Tente novamente.";
        setConflitos([{
          tipo: "erro",
          mensagem: mensagemUsuario
        }]);
      } else {
        mensagemUsuario = `Erro ao salvar horários: ${err.message}`;
        setConflitos([{
          tipo: "erro",
          mensagem: mensagemUsuario
        }]);
      }
      
      notificacao.erro(mensagemUsuario, { autoClose: false, duracao: 10000 });
      alert(`Erro ao salvar: ${mensagemUsuario}`);
    } finally {
      setSaving(false);
    }
  };

  const disciplinasParaMostrar =
    disciplinasTurma.length > 0 ? disciplinasTurma : disciplinas;

  if (error) {
    return (
      <div className="horario-editor-overlay">
        <div className="horario-editor">
          <div className="editor-error">
            <h2>Erro no Editor de Horários</h2>
            <p>{error}</p>
            <button className="btn-cancelar" onClick={onClose}>
              Fechar
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="editor-loading">Carregando editor...</div>;
  }

  return (
    <div className="horario-editor-overlay">
      <div className="horario-editor">
        <div className="editor-header">
          <h2>Editar Horário {turma?.nome ? `- ${turma.nome}` : ""}</h2>
          <button className="btn-fechar" onClick={onClose}>
            X
          </button>
        </div>

        <div className="editor-selectors">
          <div className="selector-group">
            <label>Turma:</label>
            <select
              value={selectedTurmaId || ""}
              onChange={(e) => handleTurmaChange(e.target.value)}
              className="turma-select"
            >
              <option value="">Selecione uma turma</option>
              {turmas.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.nome} {t.curso?.nome ? `(${t.curso.nome})` : ""}{" "}
                  {t.sala?.nome ? `- Sala ${t.sala.nome}` : ""}
                </option>
              ))}
            </select>
          </div>

          <div className="selector-group">
            <label>Turno:</label>
            <select
              value={turnoSelecionado}
              onChange={(e) => setTurnoSelecionado(e.target.value)}
              className="turno-select"
            >
              <option value="manha">Manhã</option>
              <option value="tarde">Tarde</option>
              <option value="noite">Noite</option>
            </select>
          </div>

          {turma && (
            <div className="turma-info-display">
              <span className="info-badge">Turma: {turma.nome}</span>
              {turma.sala?.nome && (
                <span className="info-badge">Sala: {turma.sala.nome}</span>
              )}
              {turma.curso?.nome && (
                <span className="info-badge">Curso: {turma.curso.nome}</span>
              )}
            </div>
          )}
        </div>

        {conflitos.length > 0 && (
          <div className="conflitos-container">
            {conflitos.map((c, i) => (
              <div key={i} className={`conflito-item ${c.tipo}`}>
                {c.mensagem}
              </div>
            ))}
          </div>
        )}

        {!selectedTurmaId ? (
          <div className="no-turma-selected">
            <p>Selecione uma turma acima para editar seu horário.</p>
          </div>
        ) : (
          <div className="editor-content">
            <div className="paleta-disciplinas">
              <h3>Disciplinas</h3>
              <p className="paleta-instrucao">Arraste para a grade</p>
              <div className="disciplinas-lista">
                {disciplinasParaMostrar.length === 0 ? (
                  <p className="no-disciplinas">
                    Nenhuma disciplina disponível. Vá em Disciplinas ou Turmas
                    para vincular.
                  </p>
                ) : (
                  disciplinasParaMostrar.map((disc) => {
                    const profsVinculados = professorDisciplina[disc.id] || [];
                    const profAtribuido = disciplinaProfessorTurma[disc.id];
                    const profNome = profAtribuido
                      ? profsVinculados.find((p) => p.id === profAtribuido)
                          ?.nome
                      : profsVinculados[0]?.nome;

                    return (
                      <div
                        key={disc.id}
                        className={`disciplina-item ${disc.tipo === "N" ? "nuclear" : "global"}`}
                        draggable
                        onDragStart={(e) => handleDragStart(e, disc, "paleta")}
                        onDragEnd={handleDragEnd}
                      >
                        <span className="disc-nome">{disc.nome}</span>
                        <div className="disc-info">
                          <span
                            className={`disc-tipo ${disc.tipo === "N" ? "nuclear" : "global"}`}
                          >
                            {disc.tipo === "N" ? "Nuclear" : "Global"}
                          </span>
                          {profNome && (
                            <span className="disc-prof">{profNome}</span>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            <div className="grade-horarios">
              <table className="tabela-editor">
                <thead>
                  <tr>
                    <th className="col-tempo">Tempo</th>
                    {DIAS_SEMANA.map((dia) => (
                      <th key={dia.valor}>{dia.nome}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {TEMPOS.map((tempo) => (
                    <tr key={tempo.inicio} className="">
                      <td className="col-tempo">
                        <div className="tempo-info">
                          <span className="tempo-label">{tempo.label}</span>
                          <span className="tempo-horas">
                            {tempo.inicio} - {tempo.fim}
                          </span>
                        </div>
                      </td>
                      {DIAS_SEMANA.map((dia) => {
                        const chave = `${dia.valor}-${tempo.inicio}`;
                        const horario = horarios[chave];

                        return (
                          <td
                            key={chave}
                            className={`celula-horario ${horario ? "ocupada" : "vazia"}`}
                            onDragOver={handleDragOver}
                            onDragEnter={handleDragEnter}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, dia.valor, tempo)}
                          >
                            {horario ? (
                              <div
                                className={`horario-card ${horario.disciplina?.tipo === "N" ? "nuclear" : "global"}`}
                                draggable
                                onDragStart={(e) =>
                                  handleDragStart(e, horario, "celula")
                                }
                                onDragEnd={handleDragEnd}
                              >
                                <div className="horario-disciplina">
                                  {horario.disciplina?.nome || "Sem disciplina"}
                                </div>
                                {horario.professor?.nome && (
                                  <div className="horario-professor">
                                    {horario.professor.nome}
                                  </div>
                                )}
                                <button
                                  className="btn-remover-mini"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removerHorario(dia.valor, tempo.inicio);
                                  }}
                                  title="Remover horário"
                                >
                                  ✕
                                </button>
                              </div>
                            ) : (
                              <div className="celula-vazia">
                                <div className="drop-hint">Arrastar aqui</div>
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {showNovasTurmas && (
          <div className="modal-novas-turmas">
            <div className="modal-conteudo">
              <h3>Selecione Turmas para Adicionar</h3>
              <div className="turmas-lista-selecion">
                {turmas
                  .filter((t) => t.id !== selectedTurmaId)
                  .map((t) => (
                    <label key={t.id} className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={novasTurmasSelecionadas.includes(t.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setNovasTurmasSelecionadas([
                              ...novasTurmasSelecionadas,
                              t.id,
                            ]);
                          } else {
                            setNovasTurmasSelecionadas(
                              novasTurmasSelecionadas.filter((id) => id !== t.id)
                            );
                          }
                        }}
                      />
                      <span>
                        {t.nome} {t.curso?.nome ? `(${t.curso.nome})` : ""}{" "}
                        {t.sala?.nome ? `- Sala ${t.sala.nome}` : ""}
                      </span>
                    </label>
                  ))}
              </div>
              <div className="modal-botoes">
                <button
                  className="btn-adicionar"
                  onClick={adicionarNovasTurmas}
                  disabled={novasTurmasSelecionadas.length === 0}
                >
                  Adicionar Selecionadas
                </button>
                <button
                  className="btn-cancelar-modal"
                  onClick={() => {
                    setShowNovasTurmas(false);
                    setNovasTurmasSelecionadas([]);
                  }}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="editor-footer">
          <button
            className="btn-gerar-horarios"
            onClick={gerarHorariosAutomatico}
            disabled={!selectedTurmaId || disciplinasTurma.length === 0}
            title="Gerar horários automaticamente preenchendo todos os períodos"
          >
            ⚡ Gerar Horários
          </button>
          <button
            className="btn-atualizar-professores"
            onClick={() => atualizarProfessoresVinculados(selectedTurmaId)}
            disabled={atualizandoProfessores || !selectedTurmaId}
          >
            {atualizandoProfessores
              ? "Atualizando..."
              : "🔄 Atualizar Professores"}
          </button>
          <button
            className="btn-nova-turma-footer"
            onClick={() => setShowNovasTurmas(true)}
            title="Adicionar uma nova turma para editar seus horários sem afetar as turmas já existentes"
          >
            ➕ Nova Turma
          </button>
          <button className="btn-cancelar" onClick={onClose}>
            Cancelar
          </button>
          <button
            className="btn-salvar"
            onClick={salvarHorarios}
            disabled={saving || !selectedTurmaId || !needsSave}
          >
            {saving ? "Salvando..." : "Salvar Horários"}
          </button>
        </div>
      </div>
    </div>
  );
}
